import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Container, Typography, Paper, Button, Grid, TextField,
  CircularProgress, Alert, Stepper, Step, StepLabel, Chip,
  Card, CardActionArea, CardContent, Avatar, Divider,
  InputAdornment, MenuItem, Fade, useTheme, useMediaQuery,
  MobileStepper,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PetsIcon from '@mui/icons-material/Pets';
import PersonIcon from '@mui/icons-material/Person';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LockIcon from '@mui/icons-material/Lock';
import PaymentIcon from '@mui/icons-material/Payment';
import { getCollection, findDocumentsByField } from '../services/firestoreService';
import { getAvailableSlots, createBooking, subscribeToSlots, getConsultationPrice } from '../services/bookingService';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { validateRut } from '../utils/validators';
import { REGIONES_COMUNAS } from '../utils/regionData';
import { getArancelPrice, netoDesdeBruto } from '../utils/finance';
import { toLocalDateStr, todayLocalStr } from '../utils/dateUtils';
import { trackMetaPixel } from '../services/metaPixel';
import { resolverConvenio } from '../services/presupuestoAutoService';
import { db } from '../firebase';
import SearchableSelect from '../components/SearchableSelect';
import { doc, getDoc, getDocs, collection, query, where, updateDoc, addDoc, Timestamp } from 'firebase/firestore';

const STEPS = ['Sucursal', 'Especialidad', 'Fecha', 'Hora', 'Profesional', 'Tutor', 'Mascota', 'Confirmar'];

const GENDER_OPTIONS = ['Macho', 'Hembra'];

// Genera los próximos 60 días hábiles (Lun-Sáb)
const generateAvailableDates = () => {
  const dates = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let d = new Date(today);
  d.setDate(d.getDate() + 1); // Desde mañana
  while (dates.length < 60) {
    const day = d.getDay();
    if (day !== 0) { // Excluir domingo
      dates.push(new Date(d));
    }
    d.setDate(d.getDate() + 1);
  }
  return dates;
};

const DAY_NAMES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const formatDate = (date) => {
  if (!date) return '';
  return `${DAY_NAMES[date.getDay()]} ${date.getDate()} de ${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
};

const toDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const PublicBooking = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [bookingCode, setBookingCode] = useState('');
  const [consultationPrice, setConsultationPrice] = useState(0);
  const [specialtyPricesMap, setSpecialtyPricesMap] = useState({});
  const [convenio, setConvenio] = useState(null);

  // Datos de referencia
  const [branches, setBranches] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [allProfessionals, setAllProfessionals] = useState([]);
  const [filteredProfessionals, setFilteredProfessionals] = useState([]);
  const [breeds, setBreeds] = useState([]);
  const [availableDates] = useState(generateAvailableDates);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [liveBooked, setLiveBooked] = useState({});
  const [professionalSchedules, setProfessionalSchedules] = useState([]);

  // Agenda del profesional seleccionado (para calcular disponibilidad de fechas)
  const [profScheduleDoc, setProfScheduleDoc] = useState(null);
  const [profBlocks, setProfBlocks] = useState([]);

  // Mes del calendario mostrado
  const [monthOffset, setMonthOffset] = useState(0);

  // Logs de depuración
  const [debugLogs, setDebugLogs] = useState([]);
  const [showDebug, setShowDebug] = useState(false);

  const addDebugLog = (message, data = null) => {
    const timestamp = new Date().toLocaleTimeString();
    setDebugLogs(prev => [...prev, { timestamp, message, data }]);
    console.log(message, data || '');
  };

  // Selecciones del wizard
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);

  // Pre-cargar precios de todas las especialidades cuando cambia la sucursal
  useEffect(() => {
    if (!selectedBranch?.id) { setSpecialtyPricesMap({}); return; }
    getDocs(query(
      collection(db, 'aranceles'),
      where('activo', '==', true)
    )).then(snap => {
      const map = {};
      snap.docs.forEach(d => {
        const data = d.data();
        if (data.specialtyId) {
          const price = getArancelPrice(data, selectedBranch.id);
          if (price > 0) map[data.specialtyId] = price;
        }
      });
      setSpecialtyPricesMap(map);
    }).catch(() => {});
  }, [selectedBranch]);

  // Datos del tutor
  const [ownerData, setOwnerData] = useState({ name: '', rut: '', celular: '', mail: '', region: '', comuna: '' });
  const [ownerId, setOwnerId] = useState(null);
  const [ownerFound, setOwnerFound] = useState(false);
  const [rutError, setRutError] = useState('');
  const [rutSearching, setRutSearching] = useState(false);

  // Resolver convenio del tutor cuando cambia el owner
  useEffect(() => {
    const name = ownerData.convenio || '';
    if (name) {
      resolverConvenio({ convenioName: name })
        .then((c) => setConvenio(c))
        .catch(() => setConvenio(null));
    } else {
      setConvenio(null);
    }
  }, [ownerData.convenio]);

  // Cargar precio de consulta cuando cambia especialidad, sucursal o convenio
  useEffect(() => {
    if (!selectedSpecialty?.id) {
      setConsultationPrice(0);
      return;
    }
    getConsultationPrice(selectedSpecialty.id, selectedBranch?.id || null, convenio)
      .then((price) => setConsultationPrice(price))
      .catch(() => setConsultationPrice(0));
  }, [selectedSpecialty, selectedBranch, convenio]);

  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Mascotas del tutor
  const [ownerPets, setOwnerPets] = useState([]);
  const [petsLoading, setPetsLoading] = useState(false);
  const [petMode, setPetMode] = useState('select'); // 'select' | 'new'
  const [selectedPet, setSelectedPet] = useState(null);

  // Datos de nueva mascota
  const [petData, setPetData] = useState({ name: '', species: '', breed: '', genero: '', age: '' });

  // Cargar datos iniciales
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [branchesData, specsData, profsData, breedsData, schedulesData] = await Promise.all([
          getCollection('branches').catch(() => []),
          getCollection('specialties'),
          getCollection('professionals'),
          getCollection('breeds'),
          getCollection('professionalSchedules').catch(() => []),
        ]);
        setBranches(branchesData.filter((b) => b.active !== false));
        setSpecialties(specsData.filter((s) => s.active).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        setAllProfessionals(profsData.filter((p) => p.active !== false));
        setBreeds(breedsData);
        setProfessionalSchedules(schedulesData);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Filtrar especialidades que tienen agenda online creada
  const availableSpecialties = useMemo(() => {
    const todayStr = (() => {
      const t = new Date();
      return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
    })();
    const toIsoDate = (v) => {
      if (!v) return '';
      if (typeof v === 'string') return v.split('T')[0];
      return toLocalDateStr(v);
    };
    // Una agenda es elegible si aún está vigente (no expirada) y tiene slots online activos.
    const isScheduleBookable = (s) => {
      const endStr = toIsoDate(s.endDate);
      if (endStr && todayStr > endStr) return false; // ya expiró
      const schedule = s.schedule;
      if (!schedule) return false;
      return Object.keys(schedule).some((dayKey) => {
        const day = schedule[dayKey];
        return day?.active && day?.slots?.some((slot) => slot.visibleOnline !== false);
      });
    };
    // Usar el specialtyId almacenado en la propia agenda (no el del perfil del profesional)
    const relevant = selectedBranch
      ? professionalSchedules.filter((s) => s.branchId === selectedBranch.id)
      : professionalSchedules;
    const specialtyIdsWithSchedule = new Set(
      relevant.filter(isScheduleBookable).map((s) => s.specialtyId).filter(Boolean)
    );
    return specialties.filter((s) => specialtyIdsWithSchedule.has(s.id));
  }, [specialties, professionalSchedules, selectedBranch]);

  // Filtrar profesionales al seleccionar especialidad, sucursal, fecha y hora
  useEffect(() => {
    if (!selectedSpecialty) { setFilteredProfessionals([]); return; }

    // Filtrar profesionales por especialidad usando specialtyIds (array) o specialtyId (singular, para compatibilidad)
    let filtered = allProfessionals.filter((p) => {
      const specialtyIds = p.specialtyIds || (p.specialtyId ? [p.specialtyId] : []);
      return specialtyIds.includes(selectedSpecialty.id);
    });

    // Filtrar por profesionales que tienen agenda con slots visibles online
    if (selectedBranch) {
      // Si hay sucursal seleccionada, filtrar por profesionales que tienen agenda en esa sucursal con slots online
      const professionalIdsWithOnlineSchedule = new Set();
      professionalSchedules
        .filter((s) => s.branchId === selectedBranch.id)
        .filter((s) => s.specialtyId === selectedSpecialty.id) // Filtrar también por especialidad
        .forEach((s) => {
          // Usar el campo professionalId del documento, no del ID compuesto
          const professionalId = s.professionalId;
          // Verificar que la agenda tenga al menos un slot con visibleOnline: true
          const schedule = s.schedule;
          if (schedule) {
            const dayKeys = Object.keys(schedule);
            for (const dayKey of dayKeys) {
              const daySchedule = schedule[dayKey];
              if (daySchedule?.active && daySchedule?.slots) {
                const hasOnlineSlots = daySchedule.slots.some((slot) => slot.visibleOnline !== false);
                if (hasOnlineSlots) {
                  professionalIdsWithOnlineSchedule.add(professionalId);
                  break;
                }
              }
            }
          }
        });
      filtered = filtered.filter((p) => professionalIdsWithOnlineSchedule.has(p.id));
    } else {
      // Si no hay sucursal, mostrar profesionales que tengan agenda con slots online
      const professionalIdsWithOnlineSchedule = new Set();
      professionalSchedules
        .filter((s) => s.specialtyId === selectedSpecialty.id) // Filtrar también por especialidad
        .forEach((s) => {
          // Usar el campo professionalId del documento, no del ID compuesto
          const professionalId = s.professionalId;
          // Verificar que la agenda tenga al menos un slot con visibleOnline: true
          const schedule = s.schedule;
          if (schedule) {
            const dayKeys = Object.keys(schedule);
            for (const dayKey of dayKeys) {
              const daySchedule = schedule[dayKey];
              if (daySchedule?.active && daySchedule?.slots) {
                const hasOnlineSlots = daySchedule.slots.some((slot) => slot.visibleOnline !== false);
                if (hasOnlineSlots) {
                  professionalIdsWithOnlineSchedule.add(professionalId);
                  break;
                }
              }
            }
          }
        });
      filtered = filtered.filter((p) => professionalIdsWithOnlineSchedule.has(p.id));
    }

    // Si hay fecha y hora seleccionadas, filtrar por profesionales que tienen ese slot disponible
    if (selectedDate && selectedSlot) {
      const dayOfWeek = String(selectedDate.getDay());
      filtered = filtered.filter((p) => {
        const schedule = professionalSchedules.find(
          (s) => s.professionalId === p.id && s.branchId === selectedBranch?.id && s.specialtyId === selectedSpecialty.id
        );
        if (!schedule) return false;

        const daySchedule = schedule.schedule?.[dayOfWeek];
        if (!daySchedule?.active || !daySchedule.slots?.length) return false;

        const slot = daySchedule.slots.find((s) => s.time === selectedSlot.time);
        return slot && slot.visibleOnline !== false;
      });
    }

    setFilteredProfessionals(filtered.sort((a, b) => (a.apellidos || '').localeCompare(b.apellidos || '')));
  }, [selectedSpecialty, allProfessionals, selectedBranch, selectedDate, selectedSlot, professionalSchedules]);

  // Seleccionar profesional (ahora después de seleccionar fecha y hora)
  const handleSelectProfessional = useCallback(async (prof) => {
    addDebugLog('👤 Seleccionando profesional', { id: prof.id, name: `${prof.nombre} ${prof.apellidos}` });
    try {
      setSelectedProfessional(prof);
      addDebugLog('✅ Profesional seleccionado, avanzando al paso 5');
      setActiveStep(5); // Avanzar al paso de tutor
    } catch (e) {
      addDebugLog('❌ Error al seleccionar profesional', e.message);
      console.error(e);
    }
  }, []);

  // Log cuando se entra al paso 6 (mascota)
  useEffect(() => {
    if (activeStep === 5) {
      addDebugLog('🐶 Entrando al paso 6 (Datos de la mascota)', {
        petsLoading,
        hasPets: ownerPets.length > 0,
        petMode,
        selectedPet: selectedPet?.id
      });
    }
  }, [activeStep, petsLoading, ownerPets.length, petMode, selectedPet]);

  // Cargar bloqueos cuando se selecciona especialidad y sucursal
  useEffect(() => {
    if (!selectedSpecialty || !selectedBranch) {
      setProfBlocks([]);
      return;
    }

    const today = todayLocalStr();
    const loadBlocks = async () => {
      try {
        // Obtener todos los profesionales con agenda para esta especialidad y sucursal
        const schedulesForSpecialty = professionalSchedules.filter(
          (s) => s.branchId === selectedBranch.id && s.specialtyId === selectedSpecialty.id
        );

        const professionalIds = schedulesForSpecialty.map((s) => s.professionalId);

        if (professionalIds.length === 0) {
          setProfBlocks([]);
          return;
        }

        // Cargar bloqueos para todos estos profesionales
        const blocksSnap = await getDocs(
          query(collection(db, 'scheduleBlocks'), where('professionalId', 'in', professionalIds), where('date', '>=', today))
        );
        setProfBlocks(blocksSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error(e);
        setProfBlocks([]);
      }
    };

    loadBlocks();
  }, [selectedSpecialty, selectedBranch, professionalSchedules]);

  // Limpiar selecciones al cambiar sucursal
  const handleSelectBranch = useCallback((branch) => {
    setSelectedBranch(branch);
    setSelectedSpecialty(null);
    setSelectedProfessional(null);
    setSelectedDate(null);
    setSelectedSlot(null);
    setActiveStep(1); // Avanzar automáticamente al siguiente paso
  }, []);

  // Calcular si una fecha está disponible para la especialidad y sucursal
  const isDateAvailable = useCallback((date) => {
    if (!selectedSpecialty || !selectedBranch) return false;
    const dayOfWeek = String(date.getDay());
    const dateOnlyStr = toDateString(date);
    const toIsoDate = (v) => {
      if (!v) return '';
      if (typeof v === 'string') return v.split('T')[0];
      return toLocalDateStr(v);
    };

    // Buscar agendas para la especialidad y sucursal, vigentes para la fecha consultada
    const schedulesForSpecialty = professionalSchedules.filter((s) => {
      if (s.branchId !== selectedBranch.id || s.specialtyId !== selectedSpecialty.id) return false;
      const startStr = toIsoDate(s.startDate);
      const endStr = toIsoDate(s.endDate);
      if (startStr && dateOnlyStr < startStr) return false; // aún no vigente
      if (endStr && dateOnlyStr > endStr) return false;      // ya expiró
      return true;
    });

    if (schedulesForSpecialty.length === 0) return false;

    // Verificar si al menos una agenda tiene slots activos para este día
    const hasActiveSlots = schedulesForSpecialty.some((s) => {
      const daySchedule = s.schedule?.[dayOfWeek];
      return daySchedule?.active && daySchedule.slots?.length > 0;
    });

    if (!hasActiveSlots) return false;

    // Verificar bloqueos (simplificado: verificar si hay bloqueos para cualquier profesional)
    const dateStr = toDateString(date);
    const hasFullDayBlock = profBlocks.some((b) => b.date === dateStr && b.blockType === 'full_day');

    return !hasFullDayBlock;
  }, [selectedSpecialty, selectedBranch, professionalSchedules, profBlocks]);

  // Cargar slots cuando se selecciona una fecha
  useEffect(() => {
    if (!selectedDate || !selectedSpecialty || !selectedBranch) return;
    addDebugLog('📅 Cargando slots', {
      date: toDateString(selectedDate),
      specialty: selectedSpecialty.name,
      branch: selectedBranch.name,
      dayOfWeek: selectedDate.getDay()
    });
    setSlotsLoading(true);
    setSlots([]);

    // Buscar agendas para la especialidad y sucursal, vigentes para la fecha
    const dateOnlyStr = toDateString(selectedDate);
    const toIsoDate = (v) => {
      if (!v) return '';
      if (typeof v === 'string') return v.split('T')[0];
      return toLocalDateStr(v);
    };
    const schedulesForSpecialty = professionalSchedules.filter((s) => {
      if (s.branchId !== selectedBranch.id || s.specialtyId !== selectedSpecialty.id) return false;
      const startStr = toIsoDate(s.startDate);
      const endStr = toIsoDate(s.endDate);
      if (startStr && dateOnlyStr < startStr) return false;
      if (endStr && dateOnlyStr > endStr) return false;
      return true;
    });

    addDebugLog('📋 Agendas encontradas', schedulesForSpecialty.length);

    if (schedulesForSpecialty.length === 0) {
      addDebugLog('❌ No hay agendas para esta especialidad y sucursal');
      setSlots([]);
      setSlotsLoading(false);
      return;
    }

    // Obtener slots de TODAS las agendas que coinciden, combinando por hora.
    // Cada agenda pertenece a un profesional distinto; si varios tienen el
    // mismo horario, sus capacidades se suman.
    const uniqueProfIds = [...new Set(schedulesForSpecialty.map((s) => s.professionalId))];
    addDebugLog('👤 Profesionales a consultar', uniqueProfIds);

    Promise.all(
      uniqueProfIds.map((pid) =>
        getAvailableSlots(selectedDate, pid, selectedBranch?.id, selectedSpecialty?.id)
      )
    )
      .then((results) => {
        // Combinar slots por hora, sumando capacidades y reservas
        const merged = {};
        for (const slotList of results) {
          for (const slot of slotList) {
            if (!merged[slot.time]) {
              merged[slot.time] = { time: slot.time, label: slot.label, capacity: 0, booked: 0 };
            }
            merged[slot.time].capacity += slot.capacity || 1;
            merged[slot.time].booked += slot.booked || 0;
          }
        }
        const combined = Object.values(merged).sort((a, b) => a.time.localeCompare(b.time));
        addDebugLog('✅ Slots obtenidos', { count: combined.length, slots: combined });
        setSlots(combined);
      })
      .catch((e) => {
        addDebugLog('❌ Error cargando slots', e.message);
        console.error(e);
      })
      .finally(() => setSlotsLoading(false));
  }, [selectedDate, selectedSpecialty, selectedBranch, professionalSchedules]);

  // Limpiar slot seleccionado cuando cambia la fecha
  useEffect(() => {
    setSelectedSlot(null);
  }, [selectedDate]);

  // Suscripción en tiempo real a los slots de la fecha seleccionada
  useEffect(() => {
    if (!selectedDate || !selectedSpecialty || !selectedBranch) return;

    // Buscar agendas para la especialidad y sucursal
    const schedulesForSpecialty = professionalSchedules.filter(
      (s) => s.branchId === selectedBranch.id && s.specialtyId === selectedSpecialty.id
    );

    if (schedulesForSpecialty.length === 0) return;

    // Suscribirse a los bookings de TODOS los profesionales con agenda vigente
    const uniqueProfIds = [...new Set(schedulesForSpecialty.map((s) => s.professionalId))];
    const perProfBooked = {}; // pid -> { time: count }
    const unsubs = uniqueProfIds.map((pid) =>
      subscribeToSlots(selectedDate, pid, (booked) => {
        perProfBooked[pid] = booked;
        // Combinar reservas de todos los profesionales por hora
        const merged = {};
        for (const profBooked of Object.values(perProfBooked)) {
          for (const t of Object.keys(profBooked)) {
            merged[t] = (merged[t] || 0) + profBooked[t];
          }
        }
        setLiveBooked(merged);
      }, selectedBranch.id)
    );
    return () => { unsubs.forEach((u) => u && u()); };
  }, [selectedDate, selectedSpecialty, selectedBranch, professionalSchedules]);

  // Calcular slots con disponibilidad en tiempo real
  const slotsWithLive = slots.map((s) => ({
    ...s,
    booked: liveBooked[s.time] || 0,
    available: (liveBooked[s.time] || 0) < s.capacity,
  }));

  // Días del mes mostrado
  const monthDates = useMemo(() => {
    const today = new Date();
    const currentMonth = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + monthOffset + 1, 1);

    const dates = [];
    const d = new Date(currentMonth);

    // Ajustar al primer día del mes
    d.setDate(1);

    // Agregar días vacíos al inicio si el mes no empieza en lunes
    const firstDayOfWeek = d.getDay();
    const startOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Ajustar para que lunes sea 0

    for (let i = 0; i < startOffset; i++) {
      dates.push(null); // Días vacíos
    }

    // Agregar todos los días del mes
    while (d < nextMonth) {
      const day = d.getDay();
      if (day !== 0) { // Excluir domingo
        dates.push(new Date(d));
      } else {
        dates.push(null); // Domingo como día vacío
      }
      d.setDate(d.getDate() + 1);
    }

    return dates;
  }, [monthOffset]);

  const maxMonth = 2; // Mostrar hasta 2 meses hacia adelante

  // Búsqueda de tutor por RUT
  const handleRutSearch = useCallback(async () => {
    if (!ownerData.rut) return;
    if (!validateRut(ownerData.rut)) {
      setRutError('RUT inválido. Formato: 12345678-9');
      return;
    }
    setRutError('');
    setRutSearching(true);
    try {
      const found = await findDocumentsByField('owners', 'rut', ownerData.rut);
      if (found && found.length > 0) {
        const owner = found[0];
        setOwnerData({
          name: owner.name || '',
          rut: owner.rut || '',
          celular: owner.celular || '',
          mail: owner.contact || owner.mail || '',
          region: owner.region || '',
          comuna: owner.comuna || '',
          convenio: owner.convenio || '',
        });
        setOwnerId(owner.id);
        setOwnerFound(true);
        // Cargar mascotas del tutor
        setPetsLoading(true);
        setOwnerPets([]);
        setSelectedPet(null);
        setPetMode('select');
        addDebugLog('🐶 Cargando mascotas del tutor', { ownerId: owner.id });
        try {
          const pets = await findDocumentsByField('patients', 'ownerId', owner.id);
          addDebugLog('✅ Mascotas cargadas', { count: pets?.length || 0 });
          setOwnerPets(pets || []);
        } catch (e) {
          addDebugLog('❌ Error cargando mascotas', e.message);
          console.error(e);
        } finally {
          setPetsLoading(false);
        }
      } else {
        setOwnerId(null);
        setOwnerFound(false);
        setOwnerPets([]);
        setPetMode('new');
        setSelectedPet(null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setRutSearching(false);
    }
  }, [ownerData.rut]);

  // Validaciones por paso (nuevo orden: Sucursal -> Especialidad -> Fecha -> Hora -> Profesional -> Tutor -> Mascota -> Confirmar)
  const isStepValid = () => {
    switch (activeStep) {
      case 0: return !!selectedBranch;
      case 1: return !!selectedSpecialty;
      case 2: return !!selectedDate;
      case 3: return !!selectedSlot;
      case 4: return !!selectedProfessional;
      case 5:
        return ownerData.name.trim() && ownerData.rut.trim() &&
          ownerData.celular.trim() && ownerData.mail.trim() && !rutError && ownerData.region && ownerData.comuna;
      case 6:
        if (petMode === 'new') return petData.name.trim() && petData.species;
        return !!selectedPet;
      default: return true;
    }
  };

  const handleNext = () => {
    setError('');
    setActiveStep((s) => s + 1);
  };

  const handleBack = () => {
    setError('');
    const currentStep = activeStep;

    // Limpiar selecciones al volver atrás (nuevo orden: Sucursal -> Especialidad -> Fecha -> Hora -> Profesional -> Tutor -> Mascota -> Confirmar)
    if (currentStep === 4) {
      // Volviendo de profesional a hora
      setSelectedProfessional(null);
    } else if (currentStep === 3) {
      // Volviendo de hora a fecha
      setSelectedSlot(null);
    } else if (currentStep === 2) {
      // Volviendo de fecha a especialidad
      setSelectedDate(null);
      setSelectedSlot(null);
    } else if (currentStep === 1) {
      // Volviendo de especialidad a sucursal
      setSelectedSpecialty(null);
      setSelectedProfessional(null);
      setSelectedDate(null);
      setSelectedSlot(null);
    }

    setActiveStep((s) => s - 1);
  };

  const formatPrice = (amount) =>
    new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(amount);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');

    if (!selectedBranch || !selectedSpecialty || !selectedProfessional || !selectedDate || !selectedSlot) {
      setError('Faltan datos requeridos para confirmar la reserva. Por favor completa todos los pasos.');
      setSubmitting(false);
      return;
    }

    try {
      // ─── Crear o actualizar tutor y mascota ────────────────────────────────
      let resolvedOwnerId = ownerId;
      let resolvedPetId = null;
      try {
        if (!resolvedOwnerId) {
          const ownerPayload = {
            name: ownerData.name.trim(),
            rut: ownerData.rut.trim(),
            celular: ownerData.celular.trim(),
            mail: ownerData.mail.trim(),
            contact: ownerData.mail.trim(),
            region: ownerData.region,
            comuna: ownerData.comuna,
            createdAt: Timestamp.now(),
          };
          const ownerRef = await addDoc(collection(db, 'owners'), ownerPayload);
          resolvedOwnerId = ownerRef.id;
        } else {
          await updateDoc(doc(db, 'owners', resolvedOwnerId), {
            celular: ownerData.celular,
            contact: ownerData.mail,
            mail: ownerData.mail,
            ...(ownerData.comuna ? { comuna: ownerData.comuna } : {}),
            updatedAt: Timestamp.now(),
          });
        }

        if (petMode === 'select') {
          resolvedPetId = selectedPet.id;
        } else {
          const patientPayload = {
            name: petData.name.trim(),
            species: petData.species,
            breed: petData.breed || '',
            genero: petData.genero || '',
            age: petData.age || '',
            ownerId: resolvedOwnerId,
            branchIds: [selectedBranch?.id],
            createdAt: Timestamp.now(),
          };
          const patientRef = await addDoc(collection(db, 'patients'), patientPayload);
          resolvedPetId = patientRef.id;
        }
      } catch (e) {
        console.error(e);
        setError('Error al guardar los datos del tutor o mascota. Por favor intenta nuevamente.');
        setSubmitting(false);
        return;
      }

      const petName = petMode === 'select' ? selectedPet.name : petData.name;
      const dateStr = toDateString(selectedDate);

      // Usar el clinicId del schedule que coincide con la especialidad y sucursal,
      // no el clinicId del perfil del profesional (que puede ser incorrecto cuando
      // el profesional tiene múltiples clínicas para distintas especialidades).
      const matchingSchedule = professionalSchedules.find(
        (s) => s.professionalId === selectedProfessional.id
          && s.branchId === selectedBranch?.id
          && s.specialtyId === selectedSpecialty.id
      );
      const bookingClinicId = matchingSchedule?.clinicId
        || selectedProfessional.clinicId
        || (selectedProfessional.clinicIds ? selectedProfessional.clinicIds[0] : '');
      const bookingClinicName = matchingSchedule?.clinicName || selectedProfessional.clinicName || '';

      const booking = {
        branchId: selectedBranch?.id || '',
        branchName: selectedBranch?.name || '',
        specialtyId: selectedSpecialty.id,
        specialtyName: selectedSpecialty.name,
        professionalId: selectedProfessional.id,
        professionalName: `${selectedProfessional.nombre} ${selectedProfessional.apellidos}`,
        clinicId: bookingClinicId,
        clinicName: bookingClinicName,
        date: dateStr,
        time: selectedSlot.time,
        timeLabel: selectedSlot.label,
        ownerName: ownerData.name,
        ownerRut: ownerData.rut,
        ownerId: resolvedOwnerId,
        ownerCelular: ownerData.celular,
        ownerMail: ownerData.mail,
        ownerComuna: ownerData.comuna || '',
        petName,
        petSpecies: petMode === 'select' ? (selectedPet.species || selectedPet.especie || '') : petData.species,
        petBreed: petMode === 'select' ? (selectedPet.breed || '') : petData.breed,
        petGender: petMode === 'select' ? (selectedPet.genero || selectedPet.gender || '') : petData.genero,
        petAge: petMode === 'select' ? (typeof selectedPet.age === 'number' ? `${selectedPet.age} años` : selectedPet.age || '') : petData.age,
        petId: resolvedPetId,
        convenioName: convenio?.name || ownerData.convenio || '',
        title: `${petName} - ${selectedSpecialty.name}`,
        startISO: `${dateStr}T${selectedSlot.time}:00`,
        endISO: `${dateStr}T${selectedSlot.time}:00`,
      };

      if (consultationPrice > 0) {
        // ─── FLUJO CON PAGO (Webpay) ─────────────────────────────────────────
        addDebugLog('💳 Iniciando pago Webpay', { amount: consultationPrice });

        // Meta Pixel: AddToCart al llegar al checkout
        trackMetaPixel({
          eventName: 'AddToCart',
          payload: {
            event_id: `addtocart_${selectedSpecialty.id}_${toDateString(selectedDate)}_${selectedSlot.time}`,
            value: netoDesdeBruto(consultationPrice),
            currency: 'CLP',
            content_name: selectedSpecialty.name,
            content_category: 'Agendamiento Online',
            content_ids: [selectedSpecialty.id],
            content_type: 'product',
            ...{
              external_id: ownerData.rut || '',
              first_name: ownerData.name.split(' ')[0] || '',
              last_name: ownerData.name.split(' ').slice(1).join(' ') || '',
              phone: ownerData.celular || '',
              email: ownerData.mail || '',
            },
          },
        });

        localStorage.setItem('pendingBooking', JSON.stringify({
          specialty: selectedSpecialty.name,
          date: dateStr,
          time: selectedSlot.label || selectedSlot.time,
          professional: `${selectedProfessional.nombre} ${selectedProfessional.apellidos}`,
          branchName: selectedBranch?.name || '',
          ownerName: ownerData.name,
          ownerCelular: ownerData.celular,
          ownerMail: ownerData.mail,
          ownerRut: ownerData.rut,
          petName,
          amount: consultationPrice,
        }));

        const initiateWebpay = httpsCallable(functions, 'initiateWebpayTransaction');
        const response = await initiateWebpay({
          bookingData: booking,
          amount: consultationPrice,
          ownerUpdate: { ownerId: resolvedOwnerId, celular: ownerData.celular, mail: ownerData.mail },
        });

        const { url, token } = response.data;
        addDebugLog('✅ Transacción Webpay iniciada, redirigiendo...');

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = url;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'token_ws';
        input.value = token;
        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();

      } else {
        // ─── FLUJO GRATUITO (sin pago) ────────────────────────────────────────
        addDebugLog('📋 Creando reserva gratuita (sin pago)');

        const bookingForCreate = {
          ...booking,
          start: new Date(booking.startISO),
          end: new Date(booking.endISO),
        };
        delete bookingForCreate.startISO;
        delete bookingForCreate.endISO;

        if (ownerId) {
          try {
            await updateDoc(doc(db, 'owners', ownerId), {
              celular: ownerData.celular,
              contact: ownerData.mail,
              mail: ownerData.mail,
              ...(ownerData.comuna ? { comuna: ownerData.comuna } : {}),
              updatedAt: Timestamp.now(),
            });
            addDebugLog('✅ Datos del tutor actualizados');
          } catch (e) {
            addDebugLog('⚠️ Error actualizando tutor', e.message);
          }
        }

        const code = await createBooking(bookingForCreate);
        setBookingCode(code);

        trackMetaPixel({
          eventName: 'Lead',
          payload: {
            event_id: `lead_${code}`,
            content_name: selectedSpecialty.name,
            content_category: 'Agendamiento Online',
            value: 0,
            currency: 'CLP',
            content_ids: [code],
            content_type: 'product',
            external_id: ownerData.rut || '',
            first_name: ownerData.name.split(' ')[0] || '',
            last_name: ownerData.name.split(' ').slice(1).join(' ') || '',
            phone: ownerData.celular || '',
            email: ownerData.mail || '',
          },
        });

        setActiveStep(STEPS.length);
      }
    } catch (e) {
      setError(e.message || 'Error al procesar la reserva. Por favor intenta nuevamente.');
      setSubmitting(false);
    }
  };

  // ===================== RENDER STEPS =====================

  const renderStep0 = () => (
    <Fade in>
      <Box>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>Selecciona una sucursal</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Elige la ubicación donde deseas agendar tu cita</Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : branches.length === 0 ? (
          <Alert severity="info">No hay sucursales disponibles. Por favor contacta al hospital.</Alert>
        ) : (
          <Grid container spacing={2}>
            {branches.map((b) => (
              <Grid
                key={b.id}
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <Card elevation={selectedBranch?.id === b.id ? 4 : 1} sx={{ border: selectedBranch?.id === b.id ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent', transition: 'all 0.2s', borderRadius: 3 }}>
                  <CardActionArea onClick={() => handleSelectBranch(b)} sx={{ p: 2 }}>
                    <CardContent sx={{ textAlign: 'center', p: 1 }}>
                      <Avatar sx={{ bgcolor: selectedBranch?.id === b.id ? 'primary.main' : 'grey.200', color: selectedBranch?.id === b.id ? 'white' : 'text.secondary', mx: 'auto', mb: 1, width: 48, height: 48 }}>
                        <LocationOnIcon />
                      </Avatar>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{b.name}</Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Fade>
  );

  const renderStep1 = () => {
    const selectedPrice = selectedSpecialty
      ? (specialtyPricesMap.hasOwnProperty(selectedSpecialty.id) ? specialtyPricesMap[selectedSpecialty.id] : consultationPrice)
      : 0;

    return (
      <Fade in>
        <Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>¿Qué especialidad necesitas?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Selecciona la especialidad médica para tu mascota</Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : availableSpecialties.length === 0 ? (
            <Alert severity="info">No hay especialidades con agenda disponible. Por favor contacta al hospital.</Alert>
          ) : (
            <Grid container spacing={2}>
              {availableSpecialties.map((s) => {
                const price = specialtyPricesMap.hasOwnProperty(s.id) ? specialtyPricesMap[s.id] : null;
                const isSelected = selectedSpecialty?.id === s.id;
                return (
                  <Grid
                    key={s.id}
                    size={{
                      xs: 12,
                      sm: 6,
                      md: 4
                    }}>
                    <Card elevation={isSelected ? 4 : 1} sx={{ border: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent', transition: 'all 0.2s', borderRadius: 3 }}>
                      <CardActionArea onClick={() => { setSelectedSpecialty(s); setSelectedProfessional(null); setSelectedDate(null); setSelectedSlot(null); }} sx={{ p: 2 }}>
                        <CardContent sx={{ textAlign: 'center', p: 1 }}>
                          <Avatar sx={{ bgcolor: isSelected ? 'primary.main' : 'grey.200', color: isSelected ? 'white' : 'text.secondary', mx: 'auto', mb: 1, width: 48, height: 48 }}>
                            <MedicalServicesIcon />
                          </Avatar>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>{s.name}</Typography>
                          {price > 0 && (
                            <Chip label={formatPrice(price)} size="small" color={isSelected ? 'primary' : 'default'} sx={{ mt: 1, fontWeight: 700, fontSize: '0.72rem' }} />
                          )}
                          {price === 0 && price !== null && (
                            <Chip label="Gratuita" size="small" color="success" variant="outlined" sx={{ mt: 1, fontSize: '0.72rem' }} />
                          )}
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* ── Panel de confirmación de precio ──────────────────────────────── */}
          {selectedSpecialty && (
            <Fade in>
              <Paper elevation={3} sx={{ mt: 3, p: 3, borderRadius: 3, border: '2px solid', borderColor: selectedPrice > 0 ? '#028940' : 'primary.main', bgcolor: selectedPrice > 0 ? '#f0fdf4' : 'primary.50' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CheckCircleIcon sx={{ color: selectedPrice > 0 ? '#028940' : 'primary.main' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {selectedSpecialty.name}
                  </Typography>
                </Box>

                {selectedPrice > 0 ? (
                  <>
                    {/* Precio destacado */}
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, textAlign: 'center', borderColor: '#028940' }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Valor de la consulta</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#028940', letterSpacing: 0 }}>
                        {formatPrice(selectedPrice)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        El cobro se realiza al finalizar el formulario de reserva
                      </Typography>
                    </Paper>

                    {/* Badge Webpay */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, mb: 2.5, bgcolor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                      <LockIcon sx={{ color: '#028940', fontSize: 22, flexShrink: 0 }} />
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: '#028940' }}>
                          Pago seguro con Webpay Plus
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Acepta tarjetas de crédito y débito · Transacción encriptada
                        </Typography>
                      </Box>
                    </Box>

                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={() => setActiveStep(2)}
                      sx={{ bgcolor: '#028940', '&:hover': { bgcolor: '#017030' }, borderRadius: 3, py: 1.5, fontWeight: 700, fontSize: '1rem', mb: 1 }}
                      endIcon={<ArrowForwardIcon />}
                    >
                      Acepto — Continuar con la reserva
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                      Serás dirigido al pago de {formatPrice(selectedPrice)} al confirmar la reserva
                    </Typography>
                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#fff8e1', borderRadius: 2, border: '1px solid #ffe082' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', lineHeight: 1.5 }}>
                        ⚠️ El pago de esta reserva no es reembolsable. Podrás reagendar tu cita con hasta 3 horas de anticipación llamando al{' '}
                        <strong>+56 9 3250 0867</strong>.
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Button fullWidth variant="contained" size="large" onClick={() => setActiveStep(2)} sx={{ borderRadius: 3, py: 1.5 }} endIcon={<ArrowForwardIcon />}>
                    Continuar
                  </Button>
                )}
              </Paper>
            </Fade>
          )}
        </Box>
      </Fade>
    );
  };

  const renderStep2 = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Comparar solo por fecha, no por hora
    const currentMonth = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
    const monthName = MONTH_NAMES[currentMonth.getMonth()];
    const year = currentMonth.getFullYear();

    return (
      <Fade in>
        <Box>
          {consultationPrice > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1.5, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #c3e6cb' }}>
              <PaymentIcon sx={{ color: '#028940', fontSize: 20, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ color: '#028940' }}>
                <strong>{selectedSpecialty?.name}</strong> · Costo de la consulta: <strong>{formatPrice(consultationPrice)}</strong> · Pago via Webpay
              </Typography>
            </Box>
          )}
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>Selecciona una fecha</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Horarios disponibles en <strong>{selectedSpecialty?.name}</strong> · {selectedBranch?.name}
          </Typography>

          {/* Navegación de meses */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => setMonthOffset((m) => Math.max(0, m - 1))} disabled={monthOffset === 0}>
              Anterior
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              {monthName} {year}
            </Typography>
            <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => setMonthOffset((m) => Math.min(maxMonth, m + 1))} disabled={monthOffset >= maxMonth}>
              Siguiente
            </Button>
          </Box>

          {/* Encabezado de días de la semana */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 1 }}>
            {DAY_NAMES.slice(1).map((day) => (
              <Box key={day} sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {day}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Grilla de días del mes */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
            {monthDates.map((date, index) => {
              if (!date) {
                return <Box key={`empty-${index}`} sx={{ p: 0.5 }} />;
              }

              const isSelected = selectedDate && toDateString(date) === toDateString(selectedDate);
              const isAvailable = isDateAvailable(date);
              const isPast = date < today;

              return (
                <Box key={toDateString(date)} sx={{ p: 0.5 }}>
                  <Card
                    elevation={0}
                    sx={{
                      border: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                      bgcolor: isSelected ? 'primary.50' : isAvailable && !isPast ? 'white' : 'grey.50',
                      borderRadius: 2,
                      opacity: isAvailable && !isPast ? 1 : 0.4,
                      transition: 'all 0.2s',
                      cursor: isAvailable && !isPast ? 'pointer' : 'not-allowed',
                    }}
                    onClick={() => {
                      if (isAvailable && !isPast) {
                        setSelectedDate(date);
                        setActiveStep(3);
                      }
                    }}
                  >
                    <CardContent sx={{ p: 1, textAlign: 'center', '&:last-child': { pb: 1 } }}>
                      <Typography variant="h6" sx={{ fontWeight: isSelected ? 700 : 400, fontSize: '1.1rem', color: isSelected ? 'primary.main' : 'text.primary' }}>
                        {date.getDate()}
                      </Typography>
                      {isAvailable && !isPast && (
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main', mx: 'auto', mt: 0.5 }} />
                      )}
                    </CardContent>
                  </Card>
                </Box>
              );
            })}
          </Box>

          {selectedDate && (
            <Alert severity="success" sx={{ mt: 2 }} icon={<CalendarMonthIcon />}>
              Fecha seleccionada: <strong>{formatDate(selectedDate)}</strong>
            </Alert>
          )}
        </Box>
      </Fade>
    );
  };

  const renderStep3 = () => (
    <Fade in>
      <Box>
        {consultationPrice > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1.5, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #c3e6cb' }}>
            <PaymentIcon sx={{ color: '#028940', fontSize: 20, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: '#028940' }}>
              <strong>{selectedSpecialty?.name}</strong> · Costo de la consulta: <strong>{formatPrice(consultationPrice)}</strong> · Pago via Webpay
            </Typography>
          </Box>
        )}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>Selecciona un horario</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{formatDate(selectedDate)}</Typography>

        {slotsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : slotsWithLive.length === 0 ? (
          <Alert severity="warning">
            No hay horarios disponibles para esta fecha. Por favor selecciona otra fecha.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {slotsWithLive.map((slot) => (
              <Grid
                key={slot.time}
                size={{
                  xs: 6,
                  sm: 4,
                  md: 3
                }}>
                <Card
                  elevation={0}
                  sx={{
                    border: selectedSlot?.time === slot.time
                      ? `2px solid ${theme.palette.primary.main}`
                      : '2px solid #e0e0e0',
                    borderRadius: 2,
                    opacity: slot.available ? 1 : 0.4,
                    transition: 'all 0.2s',
                    cursor: slot.available ? 'pointer' : 'not-allowed',
                    bgcolor: selectedSlot?.time === slot.time ? 'primary.50' : 'white',
                  }}
                  onClick={() => {
                    if (slot.available) {
                      setSelectedSlot(slot);
                      setActiveStep(4);
                    }
                  }}
                >
                  <CardContent sx={{ p: 2, textAlign: 'center', '&:last-child': { pb: 2 } }}>
                    <AccessTimeIcon sx={{ color: slot.available ? 'primary.main' : 'grey.400', mb: 0.5 }} />
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{slot.label || slot.time}</Typography>
                    {!slot.available && (
                      <Chip label="Ocupado" size="small" color="default" sx={{ mt: 0.5, fontSize: '0.65rem' }} />
                    )}
                    {slot.available && slot.capacity > 1 && (
                      <Typography variant="caption" color="success.main" display="block">
                        {slot.capacity - slot.booked} disponible(s)
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Fade>
  );

  const renderStep4 = () => (
    <Fade in>
      <Box>
        {consultationPrice > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1.5, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #c3e6cb' }}>
            <PaymentIcon sx={{ color: '#028940', fontSize: 20, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: '#028940' }}>
              <strong>{selectedSpecialty?.name}</strong> · Costo de la consulta: <strong>{formatPrice(consultationPrice)}</strong> · Pago via Webpay
            </Typography>
          </Box>
        )}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>Selecciona un profesional</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Profesionales disponibles para <strong>{selectedSpecialty?.name}</strong> el {formatDate(selectedDate)} a las {selectedSlot?.label || selectedSlot?.time}
        </Typography>
        {filteredProfessionals.length === 0 ? (
          <Alert severity="warning">No hay profesionales disponibles para este horario.</Alert>
        ) : (
          <Grid container spacing={2}>
            {filteredProfessionals.map((p) => (
              <Grid
                key={p.id}
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <Card elevation={selectedProfessional?.id === p.id ? 4 : 1} sx={{ border: selectedProfessional?.id === p.id ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent', transition: 'all 0.2s', borderRadius: 3 }}>
                  <CardActionArea onClick={() => handleSelectProfessional(p)} sx={{ p: 2 }}>
                    <CardContent sx={{ p: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: selectedProfessional?.id === p.id ? 'primary.main' : 'grey.300', width: 52, height: 52, fontSize: '1.1rem', fontWeight: 700 }}>
                          {(p.nombre || '?')[0]}{(p.apellidos || '')[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 700 }}>Dr/a. {p.nombre} {p.apellidos}</Typography>
                          {p.clinicName && <Typography variant="caption" color="text.secondary">{p.clinicName}</Typography>}
                        </Box>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Fade>
  );

  const renderStep5 = () => {
    return (
      <Fade in>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
            Datos del tutor
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ingresa tu RUT para verificar si ya estás registrado
          </Typography>

        {/* Búsqueda por RUT */}
        <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
          <TextField
            label="RUT"
            value={ownerData.rut}
            onChange={(e) => {
              setOwnerData({ ...ownerData, rut: e.target.value });
              setOwnerFound(false);
              if (e.target.value && !validateRut(e.target.value)) {
                setRutError('Formato: 12345678-9');
              } else {
                setRutError('');
              }
            }}
            onKeyDown={(e) => e.key === 'Enter' && handleRutSearch()}
            placeholder="Ej: 12345678-9"
            error={!!rutError}
            helperText={rutError}
            size="small"
            fullWidth
            sx={{ flex: 1 }}
          />
          <Button
            variant="outlined"
            onClick={handleRutSearch}
            disabled={rutSearching || !ownerData.rut}
            sx={{ minWidth: { xs: '100%', sm: 100 }, whiteSpace: 'nowrap' }}
          >
            {rutSearching ? <CircularProgress size={18} /> : 'Buscar'}
          </Button>
        </Box>

        {ownerFound && (
          <Alert severity="success" sx={{ mb: 0 }}>
            ¡Te encontramos! Hemos pre-llenado tus datos.
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              label="Nombre completo"
              value={ownerData.name}
              onChange={(e) => setOwnerData({ ...ownerData, name: e.target.value })}
              fullWidth required
              InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" /></InputAdornment> }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField
              label="Teléfono / Celular"
              value={ownerData.celular}
              onChange={(e) => setOwnerData({ ...ownerData, celular: e.target.value })}
              fullWidth required
              placeholder="+56 9 1234 5678"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField
              label="Correo electrónico"
              value={ownerData.mail}
              onChange={(e) => setOwnerData({ ...ownerData, mail: e.target.value })}
              fullWidth required type="email"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField select label="Región" value={ownerData.region} fullWidth required
              onChange={(e) => setOwnerData({ ...ownerData, region: e.target.value, comuna: '' })}>
              <MenuItem value="">Seleccionar región...</MenuItem>
              {Object.keys(REGIONES_COMUNAS).map(r => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <SearchableSelect label="Comuna" value={ownerData.comuna} disabled={!ownerData.region}
              onChange={(e) => setOwnerData({ ...ownerData, comuna: e.target.value })}
              options={(REGIONES_COMUNAS[ownerData.region] || []).map(c => ({ value: c, label: c }))}
              emptyLabel="Seleccionar comuna..." />
          </Grid>
        </Grid>
      </Box>
      </Fade>
    );
  };

  const renderStep6 = () => {
    const hasPets = ownerPets.length > 0;
    return (
      <Fade in>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>Datos de la mascota</Typography>

          {petsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
          ) : (
            <>
              {/* Mascotas registradas */}
              {hasPets && petMode !== 'new' && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Selecciona una mascota registrada o agrega una nueva
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    {ownerPets.map((pet) => {
                      const isSelected = petMode === 'select' && selectedPet?.id === pet.id;
                      return (
                        <Grid
                          key={pet.id}
                          size={{
                            xs: 12,
                            sm: 6
                          }}>
                          <Card
                            elevation={isSelected ? 4 : 1}
                            sx={{
                              border: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                              borderRadius: 3, transition: 'all 0.2s', cursor: 'pointer',
                            }}
                            onClick={() => { setSelectedPet(pet); setPetMode('select'); }}
                          >
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, '&:last-child': { pb: 2 } }}>
                              <Avatar sx={{ bgcolor: isSelected ? 'primary.main' : 'grey.200', width: 44, height: 44 }}>
                                <PetsIcon />
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{pet.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {[pet.species, pet.breed, pet.genero].filter(Boolean).join(' · ')}
                                </Typography>
                                {pet.age && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {typeof pet.age === 'number' ? `${pet.age} años` : pet.age}
                                  </Typography>
                                )}
                              </Box>
                              {isSelected && <CheckCircleIcon color="primary" />}
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}

                    {/* Tarjeta: Nueva mascota */}
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Card
                        elevation={petMode === 'new' ? 4 : 1}
                        sx={{
                          border: petMode === 'new' ? `2px dashed ${theme.palette.primary.main}` : '2px dashed #ccc',
                          borderRadius: 3, transition: 'all 0.2s', cursor: 'pointer',
                        }}
                        onClick={() => { setPetMode('new'); setSelectedPet(null); }}
                      >
                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, '&:last-child': { pb: 2 } }}>
                          <Avatar sx={{ bgcolor: petMode === 'new' ? 'primary.main' : 'grey.100', width: 44, height: 44 }}>
                            <AddIcon />
                          </Avatar>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: petMode === 'new' ? 'primary.main' : 'text.secondary' }}>
                            Agregar nueva mascota
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Formulario nueva mascota */}
              {(!hasPets || petMode === 'new') && (
                <Box>
                  {!hasPets && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No encontramos mascotas registradas para este tutor. Ingresa los datos.
                    </Typography>
                  )}
                  {hasPets && (
                    <Button
                      variant="text"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => setPetMode('select')}
                      sx={{ mb: 2 }}
                    >
                      Volver a mascotas registradas
                    </Button>
                  )}
                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <TextField
                        label="Nombre de la mascota" value={petData.name}
                        onChange={(e) => setPetData({ ...petData, name: e.target.value })}
                        fullWidth required
                        InputProps={{ startAdornment: <InputAdornment position="start"><PetsIcon fontSize="small" /></InputAdornment> }}
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <TextField select label="Especie" value={petData.species}
                        onChange={(e) => setPetData({ ...petData, species: e.target.value, breed: '' })} fullWidth required>
                        <MenuItem value="">Seleccionar...</MenuItem>
                        <MenuItem value="Canino">Canino</MenuItem>
                        <MenuItem value="Felino">Felino</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <TextField select label="Sexo" value={petData.genero}
                        onChange={(e) => setPetData({ ...petData, genero: e.target.value })} fullWidth>
                        <MenuItem value="">— Seleccionar —</MenuItem>
                        {GENDER_OPTIONS.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <SearchableSelect label="Raza" value={petData.breed}
                        onChange={(e) => setPetData({ ...petData, breed: e.target.value })}
                        disabled={!petData.species}
                        options={breeds
                          .filter((b) => b.species === petData.species)
                          .map((b) => ({ value: b.name, label: b.name }))}
                        emptyLabel="— Sin especificar —" />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <TextField label="Edad aproximada" value={petData.age}
                        onChange={(e) => setPetData({ ...petData, age: e.target.value })}
                        fullWidth placeholder="Ej: 3 años" />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </>
          )}
        </Box>
      </Fade>
    );
  };

  const renderStep7 = () => (
    <Fade in>
      <Box>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
          Confirma tu reserva
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Revisa los detalles antes de confirmar
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Precio a pagar */}
        {consultationPrice > 0 && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2, bgcolor: 'primary.50', borderColor: 'primary.200', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Total a pagar</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main', lineHeight: 1.2 }}>
                {formatPrice(consultationPrice)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary">Pago seguro vía</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Webpay Plus</Typography>
            </Box>
          </Paper>
        )}

        {/* Resumen */}
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Typography variant="caption" color="text.secondary">Especialidad</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedSpecialty?.name}</Typography>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Typography variant="caption" color="text.secondary">Profesional</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>Dr/a. {selectedProfessional?.nombre} {selectedProfessional?.apellidos}</Typography>
              {selectedProfessional?.clinicName && <Typography variant="caption" color="text.secondary">{selectedProfessional.clinicName}</Typography>}
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Typography variant="caption" color="text.secondary">Fecha</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatDate(selectedDate)}</Typography>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Typography variant="caption" color="text.secondary">Horario</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedSlot?.label || selectedSlot?.time}</Typography>
            </Grid>
            <Divider sx={{ width: '100%', my: 1 }} />
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Typography variant="caption" color="text.secondary">Tutor</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{ownerData.name}</Typography>
              <Typography variant="body2" color="text.secondary">{ownerData.celular} · {ownerData.mail}</Typography>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <Typography variant="caption" color="text.secondary">Mascota</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {petMode === 'select' ? selectedPet?.name : petData.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {(() => {
                  const p = petMode === 'select' ? selectedPet : petData;
                  return [p?.species, p?.breed, p?.age].filter(Boolean).join(' · ');
                })()}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Fade>
  );

  // ===================== PANTALLA DE ÉXITO =====================
  if (activeStep === STEPS.length) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 5, borderRadius: 4, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
              ¡Reserva confirmada!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Tu cita ha sido agendada exitosamente.
            </Typography>

            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="caption" color="text.secondary">Código de reserva</Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: 4, color: 'primary.main', my: 1 }}>
                {bookingCode}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Guarda este código para consultar o cancelar tu cita
              </Typography>
            </Paper>

            <Grid container spacing={1} sx={{ textAlign: 'left', mb: 3 }}>
              <Grid size={12}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <PetsIcon fontSize="small" color="primary" />
                  <Typography variant="body2"><strong>{petMode === 'select' ? selectedPet?.name : petData.name}</strong> · {selectedSpecialty?.name}</Typography>
                </Box>
              </Grid>
              <Grid size={12}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <CalendarMonthIcon fontSize="small" color="primary" />
                  <Typography variant="body2">{formatDate(selectedDate)} · {selectedSlot?.label || selectedSlot?.time}</Typography>
                </Box>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ textAlign: 'left' }}>
              Te contactaremos al número <strong>{ownerData.celular}</strong> para confirmar los detalles de tu cita.
            </Alert>
          </Paper>
        </Container>
      </Box>
    );
  }

  // ===================== LAYOUT PRINCIPAL =====================
  const stepContent = [renderStep0, renderStep1, renderStep2, renderStep3, renderStep4, renderStep5, renderStep6, renderStep7];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', display: 'flex', flexDirection: 'column' }}>
      {/* Botón de depuración */}
      <Button
        size="small"
        variant="outlined"
        onClick={() => setShowDebug(!showDebug)}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 9999,
          bgcolor: 'white',
          opacity: 0.7,
          '&:hover': { opacity: 1 }
        }}
      >
        🐛 Debug
      </Button>

      {/* Panel de depuración */}
      {showDebug && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 70,
            right: 20,
            width: isMobile ? '90%' : 350,
            maxHeight: 400,
            bgcolor: 'white',
            border: '1px solid #ccc',
            borderRadius: 2,
            p: 2,
            zIndex: 9999,
            overflow: 'auto',
            boxShadow: 3
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Logs de depuración</Typography>
          <Button size="small" onClick={() => setDebugLogs([])} sx={{ mb: 1 }}>Limpiar</Button>
          {debugLogs.map((log, i) => (
            <Box key={i} sx={{ mb: 1, fontSize: '0.75rem', borderBottom: '1px solid #eee', pb: 1 }}>
              <Typography variant="caption" color="text.secondary">{log.timestamp}</Typography>
              <Typography variant="body2">{log.message}</Typography>
              {log.data && (
                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'info.main' }}>
                  {JSON.stringify(log.data)}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Header */}
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0', py: isMobile ? 1.5 : 2, px: isMobile ? 2 : 3, flexShrink: 0 }}>
        <Container maxWidth="md" disableGutters>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1.5 : 2 }}>
            <EventAvailableIcon color="primary" sx={{ fontSize: isMobile ? 28 : 32 }} />
            <Box>
              <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 700, lineHeight: 1 }}>
                Hospital Clínico Veterinario UDLA
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Agendamiento Online
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: isMobile ? 2 : 4, flex: 1, display: 'flex', flexDirection: 'column' }} disableGutters>
        {/* Stepper desktop */}
        {!isMobile && (
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {/* Stepper mobile */}
        {isMobile && (
          <Box sx={{ mb: 2, px: 1 }}>
            <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
              Paso {activeStep + 1} de {STEPS.length}: <strong>{STEPS[activeStep]}</strong>
            </Typography>
            <MobileStepper
              variant="progress"
              steps={STEPS.length}
              position="static"
              activeStep={activeStep}
              sx={{ bgcolor: 'transparent', '& .MuiLinearProgress-root': { width: '100%', borderRadius: 4 } }}
              nextButton={<Box />}
              backButton={<Box />}
            />
          </Box>
        )}

        {/* Contenido del paso */}
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 4, mb: 3, minHeight: { xs: 'auto', md: 300 }, flex: 1 }}>
          {stepContent[activeStep]()}
        </Paper>

        {/* Navegación */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mt: 'auto' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            disabled={activeStep === 0 || submitting}
            sx={{ borderRadius: 3, flex: { xs: 1, sm: 'auto' } }}
          >
            Atrás
          </Button>

          {activeStep < STEPS.length - 1 ? (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              disabled={!isStepValid() || submitting}
              sx={{ borderRadius: 3, px: { xs: 2, sm: 4 }, flex: { xs: 1, sm: 'auto' } }}
            >
              Continuar
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
              onClick={handleSubmit}
              disabled={submitting}
              sx={{ borderRadius: 3, px: { xs: 2, sm: 4 }, flex: { xs: 1, sm: 'auto' } }}
            >
              {submitting
                ? 'Procesando...'
                : consultationPrice > 0
                ? `Pagar ${formatPrice(consultationPrice)} con Webpay`
                : 'Confirmar Reserva'}
            </Button>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default PublicBooking;
